import * as React from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Divider, Modal, notification, Row, Table } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import VoucherGroupsQuery from '../../components/Query/StaticVoucherGroups';
import VoucherGroupDetail from './VoucherGroupDetail';
import { RouteComponentProps, withRouter } from 'react-router';
import { Title } from '../../components/Layout/components';
import NotFoundPage from '../NotFound/NotFound';
import { exportRawPriceObject } from 'src/helpers/Prices/exportRawPriceObject';

interface IStaticVoucherGroupTableProps extends RouteComponentProps {
  domain: string;
  groupId?: string;
}

interface IStaticVoucherGroupTableState {
  voucherGroup?: GQL.IVoucherGroup;
}

class StaticVoucherGroupTable extends React.Component<IStaticVoucherGroupTableProps, IStaticVoucherGroupTableState> {

  public componentDidMount() {
    if ((this.props as any).onCollapse) {
      (this.props as any).onCollapse(false);
    }
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <VoucherGroupsQuery
            domain={this.props.domain}
          >
            {({ staticVoucherGroups, createStaticVoucherGroup, updateStaticVoucherGroup, deleteStaticVoucherGroup }: any) => {
              return this.renderData(
                staticVoucherGroups,
                createStaticVoucherGroup,
                updateStaticVoucherGroup,
                deleteStaticVoucherGroup,
                staticVoucherGroups.refetch
              );
            }}
          </VoucherGroupsQuery>
        </Col>
      </Row>
    );
  }

  private renderData(
    voucherGroups: any,
    createVoucherGroup: Function,
    updateVoucherGroup: Function,
    deleteVoucherGroup: Function,
    refetch: Function
  ) {
    if (voucherGroups && !voucherGroups.loading) {

      if (voucherGroups.error) {
        // todo: maybe error 5xx page !
        return (<NotFoundPage domain={this.props.domain} />);
      }

      // formulář
      if (this.props && this.props.groupId) {
        const selectedGroup = voucherGroups 
          && voucherGroups.staticVoucherGroups
            ? voucherGroups.staticVoucherGroups.find((s: GQL.IVoucherGroup) => s.id === this.props.groupId)
            : undefined;
        if (this.props.groupId !== 'new' && !selectedGroup) {
          return (<NotFoundPage domain={this.props.domain} />);
        }
        return (
          <VoucherGroupDetail
            domain={this.props.domain}
            languages={voucherGroups.languages}
            voucherGroup={voucherGroups.staticVoucherGroups.find((s: GQL.IVoucherGroup) => s.id === this.props.groupId) || {}}
            onSave={a => this.upsert(a, createVoucherGroup, updateVoucherGroup, refetch)}
            onCancel={() => this.cancel()}
          />
        );
      }

      return (
        <div>
          <Title>Static discounts</Title>
          <Divider />
          <Link className="ant-btn" to={this.getLink('new')}>Create new static discount</Link>
          <Table
            rowKey={(record: any) => record.id}
            dataSource={voucherGroups.staticVoucherGroups}
            defaultExpandAllRows={true}
            style={{ marginTop: '20px' }}
          >
            <Table.Column 
              title="Title" 
              dataIndex="title" 
              key="title" 
            />
            <Table.Column
              title="Amount"
              dataIndex="amount"
              key="amount"
              width={100}
              align="right"
              render={(u, row: GQL.IVoucherGroup) => {
                return (
                  <span>
                    {row.type === 'amount' 
                      ? row.amount
                      : `${row.amount} %`}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Active"
              dataIndex="active"
              key="active"
              width={100}
              align="center"
              render={(u, row: GQL.IVoucherGroup) => {
                return (
                  <span>
                    {row.active === true
                      ? <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                      : <CloseOutlined style={{ fontSize: 20 }} />}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              key="actions"
              width={180}
              align="center"
              render={(u: any, row: any) => this.renderActions(u, row, deleteVoucherGroup, refetch)}
            />
          </Table>
        </div>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading discounts ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(u: any, voucherGroup: GQL.IVoucherGroup, deleteVoucherGroup: Function, refetch: Function) {
    return (
      <div>
        <Link className="ant-btn" type={'dashed'} to={this.getLink(voucherGroup.id)}>
          Edit
        </Link>
        <Divider type={'vertical'} />
        <Button danger={true} onClick={() => this.delete(voucherGroup, deleteVoucherGroup, refetch)}>
          Delete
        </Button>
      </div>
    );
  }

  private delete(voucherGroup: GQL.IVoucherGroup, deleteVoucherGroup: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${voucherGroup.title}?`,
      content: (
        <span>
          Are you sure you want to delete discount <strong>{voucherGroup.title}</strong>?
        </span>
      ),
      onOk: () => {
        return deleteVoucherGroup({
          variables: {
            id: voucherGroup.id
          }
        }).then(() => refetch());
      }
    });
  }

  private upsert(
    voucherGroup: GQL.IVoucherGroup,
    createVoucherGroup: Function,
    updateVoucherGroup: Function,
    refetch: Function
  ) {
    if (voucherGroup != undefined) {

      const variables = {
        id: voucherGroup.id,
        voucherGroup: {
          title: voucherGroup.title,
          source: voucherGroup.source || 'price',
          type: voucherGroup.type || 'percent',
          applyToDiscountedGoods: voucherGroup.applyToDiscountedGoods || false,
          amount: voucherGroup.amount,
          // staré ceny
          minimumPriceOfSingleItem: voucherGroup.minimumPriceOfSingleItem,
          maximumPriceOfSingleItem: voucherGroup.maximumPriceOfSingleItem,
          minimumPriceOfOrder: voucherGroup.minimumPriceOfOrder,
          maximumPriceOfOrder: voucherGroup.maximumPriceOfOrder,
          // nové ceny
          minimumPricesOfSingleItem: voucherGroup.minimumPricesOfSingleItem
            ? voucherGroup.minimumPricesOfSingleItem.map((p: GQL.IPrice) => {
                return exportRawPriceObject(p);
              })
            : null,
          maximumPricesOfSingleItem: voucherGroup.maximumPricesOfSingleItem
            ? voucherGroup.maximumPricesOfSingleItem.map((p: GQL.IPrice) => {
                return exportRawPriceObject(p);
              })
            : null,
          minimumPricesOfOrder: voucherGroup.minimumPricesOfOrder
            ? voucherGroup.minimumPricesOfOrder.map((p: GQL.IPrice) => {
                return exportRawPriceObject(p);
              })
            : null,
          maximumPricesOfOrder: voucherGroup.maximumPricesOfOrder
            ? voucherGroup.maximumPricesOfOrder.map((p: GQL.IPrice) => {
                return exportRawPriceObject(p);
              })
            : null,
          // doprava zdarma
          freeShipping: (voucherGroup as any).deliveryMethodsIds || [],
          customerGroups: (voucherGroup as any).customerGroupsIds || [],
          active: voucherGroup.active !== null && voucherGroup.active !== undefined ? voucherGroup.active : true,
          giftId: voucherGroup.giftId,
        }
      };

      if (voucherGroup.id) {
        return updateVoucherGroup({ variables })
          .then(() => refetch())
          .then(() => this.cancel())
          .then(() =>
            notification['success']({
              message: 'Info',
              description: `Discount "${variables.voucherGroup.title}" has been updated.`
            })
          );
      }

      (variables as any).domain = this.props.domain;

      createVoucherGroup({ variables })
        .then(() => refetch())
        .then(() => this.cancel())
        .then(() =>
          notification['success']({
            message: 'Info',
            description: `Discount "${variables.voucherGroup.title}" has been created.`
          })
        );
    }
  }

  private getLink(recordId: string | null) {
    return `/${this.props.domain}/application/static-vouchers${recordId ? `/${recordId}` : ''}`;
  }

  private cancel() {
    this.props.history.push(this.getLink(null));
  }
}

export default withRouter(StaticVoucherGroupTable);



// WEBPACK FOOTER //
// ./src/scenes/StaticVoucherGroups/VoucherGroupTable.tsx